/**
 * 桌面客户端在线答题系统API服务
 */

import api from './api';

const desktopQuizAPI = {
  // ==================== 题库管理 ====================
  
  /**
   * 获取题目列表
   * @param {Object} params - 查询参数
   */
  getQuestions: (params = {}) => {
    return api.get('/desktop-quiz/questions', { params });
  },

  /**
   * 获取题目详情
   * @param {number} id - 题目ID
   */
  getQuestion: (id) => {
    return api.get(`/desktop-quiz/questions/${id}`);
  },

  /**
   * 创建题目
   * @param {Object} data - 题目数据
   */
  createQuestion: (data) => {
    return api.post('/desktop-quiz/questions', data);
  },

  /**
   * 批量导入题目
   * @param {Object} data - { text, teacher_id, school_id, subject, grade, is_shared }
   */
  importQuestions: (data) => {
    return api.post('/desktop-quiz/questions/import', data);
  },

  /**
   * 预览解析结果
   * @param {string} text - 题目文本
   */
  previewQuestions: (text) => {
    return api.post('/desktop-quiz/questions/preview', { text });
  },

  /**
   * 上传文件解析题目（预览）
   * @param {File} file - 文件对象
   */
  uploadFilePreview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/desktop-quiz/questions/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  /**
   * 上传文件导入题目
   * @param {File} file - 文件对象
   * @param {Object} options - { teacher_id, school_id, subject, grade, is_shared }
   */
  importFromFile: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    Object.keys(options).forEach(key => {
      if (options[key] != null) {
        formData.append(key, options[key]);
      }
    });
    return api.post('/desktop-quiz/questions/import-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  /**
   * 解析Word文档为HTML（保留图片和格式）
   * @param {File} file - Word文件
   */
  parseWordToHtml: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/desktop-quiz/questions/parse-word-html', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  /**
   * 更新题目
   * @param {number} id - 题目ID
   * @param {Object} data - 更新数据
   */
  updateQuestion: (id, data) => {
    return api.put(`/desktop-quiz/questions/${id}`, data);
  },

  /**
   * 删除题目
   * @param {number} id - 题目ID
   */
  deleteQuestion: (id) => {
    return api.delete(`/desktop-quiz/questions/${id}`);
  },

  /**
   * 批量删除题目
   * @param {Array} ids - 题目ID列表
   */
  batchDeleteQuestions: (ids) => {
    return api.post('/desktop-quiz/questions/batch-delete', { ids });
  },

  // ==================== 学生操作码管理 ====================
  
  /**
   * 获取班级学生操作码列表
   * @param {string} classId - 班级ID
   */
  getStudentCodes: (classId) => {
    return api.get(`/desktop-quiz/codes/${classId}`);
  },

  /**
   * 批量生成操作码
   * @param {Object} data - { classId, codeLength, validityType }
   */
  generateCodes: (data) => {
    return api.post('/desktop-quiz/codes/generate', data);
  },

  /**
   * 重新生成单个学生操作码
   * @param {number} studentId - 学生ID
   */
  regenerateCode: (studentId) => {
    return api.put(`/desktop-quiz/codes/${studentId}/regenerate`);
  },

  /**
   * 验证操作码
   * @param {Object} data - { classId, code }
   */
  verifyCode: (data) => {
    return api.post('/desktop-quiz/codes/verify', data);
  },

  // ==================== 答题会话管理 ====================
  
  /**
   * 创建答题会话
   * @param {Object} data - 会话配置
   */
  createSession: (data) => {
    return api.post('/desktop-quiz/sessions', data);
  },

  /**
   * 开始答题（推送到桌面客户端）
   * @param {number} sessionId - 会话ID
   */
  startSession: (sessionId) => {
    return api.post(`/desktop-quiz/sessions/${sessionId}/start`);
  },

  /**
   * 结束答题会话
   * @param {number} sessionId - 会话ID
   */
  endSession: (sessionId) => {
    return api.post(`/desktop-quiz/sessions/${sessionId}/end`);
  },

  /**
   * 提交答案
   * @param {number} sessionId - 会话ID
   * @param {Object} data - { questionId, studentCode, answer, answerTime }
   */
  submitAnswer: (sessionId, data) => {
    return api.post(`/desktop-quiz/sessions/${sessionId}/submit`, data);
  },

  /**
   * 获取会话统计
   * @param {number} sessionId - 会话ID
   */
  getSessionStatistics: (sessionId) => {
    return api.get(`/desktop-quiz/sessions/${sessionId}/statistics`);
  },

  // ==================== 积分排行榜 ====================
  
  /**
   * 获取班级积分排行榜
   * @param {string} classId - 班级ID
   * @param {Object} params - { limit }
   */
  getLeaderboard: (classId, params = {}) => {
    return api.get(`/desktop-quiz/leaderboard/${classId}`, { params });
  },

  /**
   * 获取积分规则模板
   * @param {Object} params - { teacherId, schoolId }
   */
  getScoreTemplates: (params = {}) => {
    return api.get('/desktop-quiz/score-templates', { params });
  },

  /**
   * 推送题目到班级（支持定时）
   * @param {Object} data - { class_id, question_ids, allow_class_start, title, schedule_at }
   */
  pushQuestions: (data) => {
    return api.post('/desktop-quiz/push-questions', data);
  },

  /**
   * 班级端开始答题（允许自启时）
   * @param {Object} data - { question_ids, title }
   */
  classStartSession: (data) => {
    return api.post('/desktop-quiz/sessions/class-start', data);
  }
};

export default desktopQuizAPI;

